/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Machine, assign, AssignAction } from 'xstate';

import {
  GenericContext,
  GenericEvents,
  GenericStateSchema,
  IDocs,
  ErrorsFlowTypes,
} from '../types';
import { getReturnResponseDefault } from './getReturnResponseDefault';
import { prepareGenericData } from './prepareDataMachine';
import { coreServiceResponseFactory } from './serviceResponseFactory';

const hasErrors = (errors: ErrorsFlowTypes | undefined) => {
  return (
    undefined !== errors &&
    Object.keys(errors).length > 0 &&
    Object.values(errors).includes(true)
  );
};

const handlePrepareData = (params: {
  ctx: GenericContext;
  definitionsAPI: any;
  handleResponse?: any;
}) => {
  const { ctx, definitionsAPI, handleResponse } = params;

  const keys = Object.keys(ctx);
  const genericData = prepareGenericData({
    contexts: [ctx.invokeApiResponse],
    keys,
    definitions: [definitionsAPI],
  });

  const { data, headers, infos, errors } = genericData;

  const hasNoErrors = errors?.length === 0;
  let dataResponse = {};
  if (hasNoErrors) {
    dataResponse = data;
    if (
      handleResponse &&
      typeof handleResponse.handleSucessFinalResponseFunction === 'function'
    ) {
      dataResponse = handleResponse.handleSucessFinalResponseFunction(
        data,
        ctx.inputParameters,
        headers,
      );
    }
  } else {
    dataResponse = { smilesErrorService: true };
    if (
      handleResponse &&
      handleResponse.handleErrorFlowFinalResponseFunction &&
      typeof handleResponse.handleErrorFlowFinalResponseFunction === 'function'
    ) {
      dataResponse = handleResponse.handleErrorFlowFinalResponseFunction(
        infos,
        { headers },
        ctx.invokeApiResponse?.serviceResponse,
      );
    }
  }

  return dataResponse;
};

const assignInputParams: AssignAction<GenericContext, GenericEvents> = assign({
  inputParameters: (_context, event) => event,
});

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const genericMachine = (params: {
  invokeApiFunction: any;
  definitionsAPI: any;
  mocksService?: any[];
  dataFields?: any;
  handleResponse?: {
    handleSucessFinalResponseFunction?: any;
    handleErrorFlowFinalResponseFunction?: any;
  };
  validateInputParameters?: (inputParams: any) => ErrorsFlowTypes;
}) => {
  const {
    invokeApiFunction,
    definitionsAPI,
    handleResponse,
    mocksService,
    dataFields,
    validateInputParameters,
  } = params;

  const Docs: IDocs = {
    apis: [
      {
        name: definitionsAPI.API_DOMAIN,
        path: definitionsAPI.ENDPOINT_API_PATH,
        linkPostman: definitionsAPI.URL_POSTMAN,
        order: 1,
      },
    ],
    mocksService,
    dataFields,
  };

  const machine = () =>
    Machine<GenericContext, GenericStateSchema, GenericEvents>(
      {
        id: `generic-machine-${Math.random()}`,
        initial: 'IDLE',
        context: {
          inputParameters: null,
          invokeApiResponse: null,
          finalResponse: null,
          validationFlags: {},
        },
        states: {
          IDLE: {
            on: {
              START: {
                target: 'INVOKE_API',
                actions: 'assignInputParams',
              },
            },
          },
          INVOKE_API: {
            invoke: {
              id: 'api-invoke',
              src: (ctx, state) => {
                if (validateInputParameters) {
                  ctx.validationFlags = validateInputParameters(
                    ctx.inputParameters,
                  );
                  if (ctx.validationFlags && hasErrors(ctx.validationFlags)) {
                    return Promise.reject({
                      ...getReturnResponseDefault(),
                      success: false,
                      errors: {
                        flow: { validationErrors: ctx.validationFlags },
                        infos: null,
                        details: null,
                      },
                    });
                  }
                }
                return invokeApiFunction(state, ctx);
              },
              onDone: {
                target: 'PREPARE_DATA',
                actions: assign({
                  invokeApiResponse: (_, state) => state.data,
                }),
              },
              onError: [
                {
                  target: 'FINISH',
                  actions: assign({
                    finalResponse: (_, state) => {
                      return state.data;
                    },
                  }),
                  cond: (context) => {
                    return hasErrors(context?.validationFlags);
                  },
                },
                {
                  target: 'PREPARE_DATA',
                  actions: assign({
                    invokeApiResponse: (_, state) => {
                      return state.data;
                    },
                  }),
                },
              ],
            },
          },
          PREPARE_DATA: {
            id: 'prepareData',
            always: [
              {
                id: 'preparing',
                target: 'FINISH',
                actions: assign({
                  finalResponse: (ctx) =>
                    coreServiceResponseFactory({
                      apiResponses: ctx.invokeApiResponse
                        ? [ctx.invokeApiResponse]
                        : [],
                      definitions: [definitionsAPI],
                      contextKeys: Object.keys(ctx),
                      finalResponseFn: () =>
                        handlePrepareData({
                          ctx,
                          definitionsAPI,
                          handleResponse,
                        }),
                    }),
                }),
              },
            ],
          },
          FINISH: {
            type: 'final',
          },
        },
      },
      {
        actions: { assignInputParams },
      },
    );

  return { machine, Docs };
};
